import { create } from "zustand";
import { devtools } from "zustand/middleware";

import type { Employee } from "~/providers/employee-provider";

interface PermissionStoreState {
  noPermissionOpen: boolean;
  isSettingTag: boolean;
  currentEmployee?: Employee;
  signInOpen: boolean;
  signInLabel?: string;
  signInResolver?: {
    resolve?: (employee: Employee) => void;
    reject?: () => void;
  };
  reset: () => void;
  setNoPermissionOpen: (noPermissionOpen: boolean) => void;
  setSignInOpen: ({
    open,
    label,
    resolve,
    reject,
  }: {
    open: boolean;
    label?: string;
    resolve?: (employee: Employee) => void;
    reject?: () => void;
  }) => void;
  setCurrentEmployee: (currentEmployee?: Employee) => void;
  setIsSettingTag: (isSettingTag: boolean) => void;
  getCurrentEmployee: () => Employee | undefined;
  completeSignIn: (employee: Employee) => void;
  cancelSignIn: () => void;
}

const initialState = {
  noPermissionOpen: false,
  profileOpen: false,
  isSettingTag: false,
  signInOpen: false,
  currentEmployee: undefined,
  signInResolver: undefined,
};

export const usePermissionStore = create<PermissionStoreState>()(
  devtools((set, get) => ({
    ...initialState,
    reset: () => set(initialState),
    setIsSettingTag: (isSettingTag) => {
      set(() => ({ isSettingTag }));
    },
    setNoPermissionOpen(noPermissionOpen) {
      set(() => ({ noPermissionOpen }));
    },
    setCurrentEmployee(currentEmployee) {
      set(() => ({ currentEmployee }));
    },
    setSignInOpen: ({ open, label, resolve, reject }) => {
      set(() => ({
        signInOpen: open,
        signInLabel: label,
        signInResolver: open === true ? { resolve, reject } : undefined, // Set or clear resolvers
      }));
    },
    completeSignIn: (employee) =>
      set((state) => {
        if (state.signInResolver?.resolve) {
          state.signInResolver.resolve(employee);
        }
        return {
          currentEmployee: employee,
          signInResolver: undefined,
          signInOpen: false,
          signInLabel: undefined,
        };
      }),
    cancelSignIn: () =>
      set((state) => {
        if (state.signInResolver?.reject) {
          state.signInResolver.reject(); // Reject the promise if sign-in is canceled
        }
        return {
          signInResolver: undefined,
          signInOpen: false,
          signInLabel: undefined,
        };
      }),
    getCurrentEmployee() {
      return get().currentEmployee;
    },
  })),
);
